import { useEffect, useState, useCallback, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "./ui/command";
import {
  Car, Bike, Droplets, Wrench, Plus, Trash2, AlertTriangle, Gauge, Loader2, Coins, CalendarDays, Image as ImageIcon, Search, X, ChevronsUpDown, Check,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ImageZoom } from "./ImageZoom";

const fmt = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n || 0);

const OilDialog = ({ open, onOpenChange, vehicle, limit, onDone }) => {
  const [odo, setOdo] = useState("");
  const [cost, setCost] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setOdo(""); setCost(""); setDate(new Date().toISOString().slice(0, 10)); } }, [open]);
  const submit = async () => {
    if (!odo || parseFloat(odo) <= 0) { toast.warning("أدخل قراءة العدّاد"); return; }
    setBusy(true);
    try {
      const r = await api.maintenanceOilChange({
        vehicle_number: vehicle.vehicle_number, vehicle_type: vehicle.vehicle_type,
        odometer: parseFloat(odo), cost: parseFloat(cost || "0"), date,
      });
      toast.success(r.over_limit ? `تم التسجيل — ⚠️ تجاوز الحد (${r.km_since} كم)` : "تم تسجيل تغيير الزيت");
      onDone(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التسجيل"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="sm:max-w-sm">
        <DialogHeader className="text-start">
          <DialogTitle className="font-heading">تسجيل تغيير زيت — {vehicle?.vehicle_number}</DialogTitle>
          <DialogDescription className="text-start text-xs">الحد المسموح كل {limit} كم. لن يُقبل التسجيل إن كانت المسافة أقل من الحد.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>قراءة العدّاد الحالية (كم)</Label>
            <Input data-testid="oil-odometer-input" type="number" dir="ltr" value={odo} onChange={(e) => setOdo(e.target.value)} placeholder="مثال: 45200" /></div>
          <div className="space-y-1.5"><Label>التكلفة (⃁)</Label>
            <Input data-testid="oil-cost-input" type="number" dir="ltr" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0" /></div>
          <div className="space-y-1.5"><Label>التاريخ</Label>
            <Input data-testid="oil-date-input" type="date" dir="ltr" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <Button data-testid="oil-submit-btn" disabled={busy} onClick={submit} className="w-full bg-blue-600 hover:bg-blue-700 gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Droplets className="h-4 w-4" />} تسجيل
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const RecordDialog = ({ open, onOpenChange, vehicle, onDone }) => {
  const [part, setPart] = useState("");
  const [cost, setCost] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setPart(""); setCost(""); setDate(new Date().toISOString().slice(0, 10)); } }, [open]);
  const submit = async () => {
    if (!part.trim()) { toast.warning("أدخل نوع القطعة"); return; }
    setBusy(true);
    try {
      await api.maintenanceAddRecord({ vehicle_number: vehicle.vehicle_number, vehicle_type: vehicle.vehicle_type, part_type: part, change_date: date, cost: parseFloat(cost || "0") });
      toast.success("تمت إضافة سجل الصيانة"); onDone(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="sm:max-w-sm">
        <DialogHeader className="text-start"><DialogTitle className="font-heading">إضافة صيانة — {vehicle?.vehicle_number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>نوع القطعة / الصيانة</Label>
            <Input data-testid="rec-part-input" value={part} onChange={(e) => setPart(e.target.value)} placeholder="مثال: فلتر هواء، إطارات، فرامل" /></div>
          <div className="space-y-1.5"><Label>تاريخ التغيير</Label>
            <Input data-testid="rec-date-input" type="date" dir="ltr" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>التكلفة (⃁)</Label>
            <Input data-testid="rec-cost-input" type="number" dir="ltr" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0" /></div>
          <Button data-testid="rec-submit-btn" disabled={busy} onClick={submit} className="w-full bg-blue-600 hover:bg-blue-700 gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />} حفظ
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const VehicleDetail = ({ open, onOpenChange, vehicle, showPurchase, showMaintenance, oilReadOnly, limit, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purchase, setPurchase] = useState("");
  const [oilOpen, setOilOpen] = useState(false);
  const [recOpen, setRecOpen] = useState(false);

  const load = useCallback(async () => {
    if (!vehicle) return;
    setLoading(true);
    try { const d = await api.maintenanceVehicle(vehicle.vehicle_number); setDetail(d); setPurchase(String(d.purchase_value || "")); }
    catch { toast.error("تعذّر تحميل التفاصيل"); }
    finally { setLoading(false); }
  }, [vehicle]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const savePurchase = async () => {
    try { await api.maintenanceSetMeta(vehicle.vehicle_number, { purchase_value: parseFloat(purchase || "0"), vehicle_type: vehicle.vehicle_type }); toast.success("تم حفظ قيمة الشراء"); onChanged?.(); }
    catch { toast.error("تعذّر الحفظ"); }
  };
  const delOil = async (id) => { await api.maintenanceDelOil(id); load(); onChanged?.(); };
  const delRec = async (id) => { await api.maintenanceDelRecord(id); load(); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="sm:max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader className="text-start">
          <DialogTitle className="font-heading flex items-center gap-2">
            <Gauge className="h-5 w-5 text-blue-600" /> {vehicle?.vehicle_number}
            {vehicle?.driver && <span className="text-sm font-normal text-slate-400">· {vehicle.driver}</span>}
          </DialogTitle>
          <DialogDescription className="text-start text-xs">حد تغيير الزيت كل {limit} كم</DialogDescription>
        </DialogHeader>
        {loading || !detail ? (
          <div className="flex justify-center py-10 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            {showPurchase && (
              <div className="flex items-end gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex-1 space-y-1"><Label className="text-xs flex items-center gap-1"><Coins className="h-3.5 w-3.5 text-emerald-600" /> قيمة شراء السيارة (⃁)</Label>
                  <Input data-testid="purchase-value-input" type="number" dir="ltr" value={purchase} onChange={(e) => setPurchase(e.target.value)} className="bg-white" /></div>
                <Button data-testid="save-purchase-btn" onClick={savePurchase} className="bg-emerald-600 hover:bg-emerald-700">حفظ</Button>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="font-heading font-bold text-sm flex items-center gap-1.5"><Droplets className="h-4 w-4 text-blue-600" /> سجل تغيير الزيت</div>
                {!oilReadOnly && <Button data-testid="add-oil-btn" size="sm" onClick={() => setOilOpen(true)} className="h-8 bg-blue-600 hover:bg-blue-700 gap-1"><Plus className="h-3.5 w-3.5" /> إضافة</Button>}
              </div>
              {detail.oil_changes.length === 0 ? <p className="text-xs text-slate-400 py-2">لا يوجد سجل بعد.</p> : (
                <div className="overflow-x-auto rounded-xl border border-slate-100">
                  <table className="w-full text-sm text-right"><thead className="bg-slate-50 text-xs text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-right whitespace-nowrap font-medium">التاريخ</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap font-medium">العدّاد</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap font-medium">المسافة</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap font-medium">التكلفة</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap font-medium">المصدر</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap font-medium">الصورة</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap font-medium">الفاتورة</th>
                      <th className="px-3 py-2 w-full"></th>
                    </tr>
                  </thead><tbody className="font-mono-nums">
                    {detail.oil_changes.map((o) => (
                      <tr key={o.id} className={`border-t border-slate-100 ${o.over_limit ? "bg-rose-50" : ""}`}>
                        <td className="px-3 py-2 font-sans whitespace-nowrap">{o.date}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{fmt(o.odometer)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{o.km_since == null ? "—" : `${fmt(o.km_since)} كم`} {o.over_limit && <AlertTriangle className="inline h-3.5 w-3.5 text-rose-500" />}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{fmt(o.cost)}</td>
                        <td className="px-3 py-2 font-sans text-xs text-slate-500 whitespace-nowrap">{o.source === "portal" ? "المندوب" : o.source === "workshop" ? "الورشة" : "المدير"}</td>
                        <td className="px-3 py-2">
                          {o.attachment_id ? (
                            <ImageZoom src={api.fileViewUrl(o.attachment_id)} alt="صورة العدّاد"
                              label={`${vehicle?.vehicle_number || ""} · ${fmt(o.odometer)} كم · ${o.date}`}
                              testid={`oil-photo-${o.id}`} />
                          ) : <span className="text-slate-300"><ImageIcon className="h-4 w-4" /></span>}
                        </td>
                        <td className="px-3 py-2">{o.invoice_id ? <ImageZoom src={api.fileViewUrl(o.invoice_id)} alt="الفاتورة" label={`${vehicle?.vehicle_number || ""} · فاتورة · ${o.date}`} testid={`oil-inv-${o.id}`} /> : null}</td>
                        <td className="px-3 py-2 w-full text-left"><button data-testid={`del-oil-${o.id}`} onClick={() => delOil(o.id)} className="text-slate-300 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button></td>
                      </tr>
                    ))}
                  </tbody></table>
                </div>
              )}
            </div>

            {detail.oil_changes.some((o) => o.attachment_id) && (
              <div data-testid="odometer-gallery">
                <div className="font-heading font-bold text-sm flex items-center gap-1.5 mb-2"><ImageIcon className="h-4 w-4 text-blue-600" /> معرض صور العدّاد</div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {detail.oil_changes.filter((o) => o.attachment_id).map((o) => (
                    <div key={o.id} className="space-y-1">
                      <ImageZoom src={api.fileViewUrl(o.attachment_id)} alt="صورة العدّاد"
                        label={`${vehicle?.vehicle_number || ""} · ${fmt(o.odometer)} كم · ${o.date}`}
                        testid={`gallery-photo-${o.id}`}
                        thumbClassName="h-20 w-full object-cover rounded-lg border border-slate-200 hover:ring-2 hover:ring-blue-400" />
                      <div className="text-[10px] text-center text-slate-500 font-mono-nums">{fmt(o.odometer)} · {o.date}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {showMaintenance && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-heading font-bold text-sm flex items-center gap-1.5"><Wrench className="h-4 w-4 text-amber-600" /> سجل الصيانة والقطع</div>
                  <Button data-testid="add-rec-btn" size="sm" onClick={() => setRecOpen(true)} className="h-8 bg-amber-600 hover:bg-amber-700 gap-1"><Plus className="h-3.5 w-3.5" /> إضافة</Button>
                </div>
                {detail.maintenance.length === 0 ? <p className="text-xs text-slate-400 py-2">لا يوجد سجل بعد.</p> : (
                  <div className="overflow-x-auto rounded-xl border border-slate-100">
                    <table className="w-full text-sm text-right"><thead className="bg-slate-50 text-xs text-slate-500">
                      <tr>
                        <th className="px-3 py-2 text-right whitespace-nowrap font-medium">نوع القطعة</th>
                        <th className="px-3 py-2 text-right whitespace-nowrap font-medium">تاريخ التغيير</th>
                        <th className="px-3 py-2 text-right whitespace-nowrap font-medium">التكلفة</th>
                        <th className="px-3 py-2 text-right whitespace-nowrap font-medium">الفاتورة</th>
                        <th className="px-3 py-2 w-full"></th>
                      </tr>
                    </thead><tbody>
                      {detail.maintenance.map((m) => (
                        <tr key={m.id} className="border-t border-slate-100">
                          <td className="px-3 py-2 whitespace-nowrap">{m.part_type}</td>
                          <td className="px-3 py-2 font-mono-nums whitespace-nowrap">{m.change_date}</td>
                          <td className="px-3 py-2 font-mono-nums whitespace-nowrap">{fmt(m.cost)} ⃁</td>
                          <td className="px-3 py-2">{m.attachment_id ? <ImageZoom src={api.fileViewUrl(m.attachment_id)} alt="الفاتورة" label={`${vehicle?.vehicle_number || ""} · ${m.part_type}`} testid={`rec-inv-${m.id}`} /> : <span className="text-slate-300"><ImageIcon className="h-4 w-4" /></span>}</td>
                          <td className="px-3 py-2 w-full text-left"><button data-testid={`del-rec-${m.id}`} onClick={() => delRec(m.id)} className="text-slate-300 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button></td>
                        </tr>
                      ))}
                    </tbody></table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <OilDialog open={oilOpen} onOpenChange={setOilOpen} vehicle={vehicle} limit={limit} onDone={() => { load(); onChanged?.(); }} />
        <RecordDialog open={recOpen} onOpenChange={setRecOpen} vehicle={vehicle} onDone={load} />
      </DialogContent>
    </Dialog>
  );
};

export const MaintenancePanel = ({ type, showPurchase = false, showMaintenance = false, oilReadOnly = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [costs, setCosts] = useState(null);
  const [qVeh, setQVeh] = useState("");
  const [qVehOpen, setQVehOpen] = useState(false);
  const [qOdo, setQOdo] = useState("");
  const [qBusy, setQBusy] = useState(false);
  const [qPart, setQPart] = useState("");
  const [qPartCost, setQPartCost] = useState("");
  const [qQty, setQQty] = useState("1");
  const [stock, setStock] = useState([]);
  const loadStock = useCallback(() => { api.spareParts().then((r) => setStock(Array.isArray(r) ? r : r?.items || [])).catch(() => {}); }, []);
  useEffect(() => { if (type === "moto") loadStock(); }, [type, loadStock]);
  const stockNames = useMemo(() => {
    const m = {};
    stock.forEach((p) => { if (Number(p.quantity) > 0) m[p.name] = (m[p.name] || 0) + Number(p.quantity); });
    return Object.entries(m);
  }, [stock]);
  const [search, setSearch] = useState("");
  const [fState, setFState] = useState("all");
  const isMoto = type === "moto";

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.maintenanceVehicles(type)); }
    catch { toast.error("تعذّر تحميل المركبات"); }
    finally { setLoading(false); }
  }, [type]);
  const loadCosts = useCallback(async () => {
    try { setCosts(await api.maintenanceCosts(type, 6)); } catch { /* ignore */ }
  }, [type]);
  useEffect(() => { load(); loadCosts(); }, [load, loadCosts]);

  const limit = data?.limit || (isMoto ? 1000 : 4500);
  const overCount = (data?.vehicles || []).filter((v) => v.over_limit_recent).length;
  const filteredVehicles = (() => {
    const q = search.trim().toLowerCase();
    const all = (data?.vehicles || []).filter((v) => fState === "all" || (fState === "over" ? v.over_limit_recent
      : fState === "tracked" ? (v.oil_count && !v.over_limit_recent) : !v.oil_count));
    if (!q) return all;
    return all.filter((v) =>
      String(v.vehicle_number || "").toLowerCase().includes(q) ||
      String(v.driver || "").toLowerCase().includes(q));
  })();
  const vs = data?.vehicles || [];
  const mKpis = [["all", "كل المركبات", vs.length, "text-slate-800"], ["tracked", "مُتابَعة", vs.filter((v) => v.oil_count && !v.over_limit_recent).length, "text-emerald-600"],
    ["over", "تجاوزت الحد", overCount, "text-rose-600"], ["none", "بدون سجل", vs.filter((v) => !v.oil_count).length, "text-slate-500"]];

  const quickOil = async () => {
    if (!qVeh) { toast.error("اختر المركبة"); return; }
    if (!qOdo || Number(qOdo) <= 0) { toast.error("أدخل قراءة العدّاد"); return; }
    setQBusy(true);
    try {
      const v = (data?.vehicles || []).find((x) => x.vehicle_number === qVeh);
      const r = await api.maintenanceOilChange({ vehicle_number: qVeh, vehicle_type: v?.vehicle_type || (isMoto ? "دراجة نارية" : "سيارة"),
        odometer: parseFloat(qOdo), cost: 0, date: new Date().toISOString().slice(0, 10), skip_limit: true });
      toast.success(`تم تسجيل تغيير الزيت — ${qVeh}${r.km_since ? ` (${fmt(r.km_since)} كم منذ آخر تغيير)` : ""}`);
      setQOdo(""); setQVeh(""); load(); loadCosts();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التسجيل"); }
    finally { setQBusy(false); }
  };
  const quickPart = async () => {
    if (!qVeh) { toast.error("اختر الدبّاب"); return; }
    if (!qPart.trim()) { toast.error("اكتب اسم القطعة"); return; }
    setQBusy(true);
    try {
      const v = (data?.vehicles || []).find((x) => x.vehicle_number === qVeh);
      const r = await api.maintenanceAddRecord({ vehicle_number: qVeh, vehicle_type: v?.vehicle_type || (isMoto ? "دراجة نارية" : "سيارة"),
        part_type: qPart.trim(), quantity: parseFloat(qQty || "1") || 1, change_date: new Date().toISOString().slice(0, 10), cost: parseFloat(qPartCost || "0") });
      toast.success(`تم تسجيل تغيير "${qPart.trim()}" — ${qVeh}${r.from_stock ? " وخُصمت من المخزون" : " وأُضيفت لسجل الاستهلاك"}`);
      setQPart(""); setQPartCost(""); setQQty("1"); setQVeh(""); loadCosts(); loadStock();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التسجيل"); }
    finally { setQBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid={`maintenance-panel-${type}`}>
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-l from-blue-50 to-white p-4 flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-blue-600/10 flex items-center justify-center">{isMoto ? <Bike className="h-6 w-6 text-blue-600" /> : <Car className="h-6 w-6 text-blue-600" />}</div>
        <div className="flex-1">
          <div className="font-heading font-extrabold text-slate-900">{isMoto ? "صيانة الدراجات النارية" : "صيانة السيارات"}</div>
          <p className="text-xs text-slate-500">تغيير الزيت مسموح كل <span className="font-bold text-blue-600 font-mono-nums">{fmt(limit)}</span> كم — يُحسب تلقائيًا من قراءة العدّاد.</p>
        </div>
      </div>

      {isMoto && !oilReadOnly && (data?.vehicles || []).length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="quick-oil-card">
          <div className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-800"><Droplets className="h-4 w-4 text-amber-500" /> تسجيل سريع: تغيير زيت أو قطعة</div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_140px_auto]">
            <Popover open={qVehOpen} onOpenChange={setQVehOpen}>
              <PopoverTrigger asChild>
                <button type="button" role="combobox" data-testid="quick-oil-vehicle"
                  className="flex h-11 items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm text-right">
                  <span className={qVeh ? "text-slate-900 font-semibold" : "text-slate-400"}>
                    {qVeh ? (() => { const v = (data?.vehicles || []).find((x) => x.vehicle_number === qVeh); return v ? `${v.vehicle_number}${v.driver ? ` — ${v.driver}` : ""}` : qVeh; })() : "اختر الدبّاب…"}
                  </span>
                  <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-400" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[240px]" data-testid="quick-oil-vehicle-popover">
                <Command>
                  <CommandInput placeholder="ابحث برقم الدبّاب أو اسم السائق…" data-testid="quick-oil-vehicle-search" className="text-right" />
                  <CommandList>
                    <CommandEmpty>لا توجد نتائج مطابقة</CommandEmpty>
                    <CommandGroup>
                      {(data?.vehicles || []).map((v) => (
                        <CommandItem key={v.vehicle_number} value={`${v.vehicle_number} ${v.driver || ""}`}
                          data-testid={`quick-oil-vehicle-opt-${v.vehicle_number}`}
                          onSelect={() => { setQVeh(v.vehicle_number); setQVehOpen(false); }}
                          className="flex items-center justify-between gap-2 cursor-pointer">
                          <span className="flex flex-col">
                            <span className="font-semibold text-slate-800" dir="ltr">{v.vehicle_number}</span>
                            {v.driver && <span className="text-xs text-slate-400">{v.driver}</span>}
                          </span>
                          {qVeh === v.vehicle_number && <Check className="h-4 w-4 text-blue-600" />}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <Input data-testid="quick-oil-odometer" type="number" dir="ltr" value={qOdo} onChange={(e) => setQOdo(e.target.value)}
              placeholder="قراءة العدّاد" className="h-11" />
            <Button data-testid="quick-oil-submit" disabled={qBusy} onClick={quickOil} className="h-11 gap-1.5 bg-amber-500 hover:bg-amber-600">
              {qBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Droplets className="h-4 w-4" />} تم تغيير الزيت
            </Button>
          </div>
          <p className="mt-1.5 text-[11px] text-slate-400">يُسجَّل فورًا في سجل المركبة، ويُحسب الفرق منذ آخر تغيير. بدون حدٍّ للمسافة — يمكن التسجيل في أي وقت.</p>
          <div className="mt-3 grid grid-cols-1 gap-2 border-t border-dashed border-slate-200 pt-3 sm:grid-cols-[1fr_80px_110px_auto]" data-testid="quick-part-row">
            <Input data-testid="quick-part-name" list="quick-part-stock" value={qPart} onChange={(e) => setQPart(e.target.value)} onKeyDown={(e) => e.key === "Enter" && quickPart()}
              placeholder="اسم القطعة — اختر من المخزون أو اكتب (مثال: فحمات، بطارية)" className="h-11" />
            <datalist id="quick-part-stock">{stockNames.map(([n, q]) => <option key={n} value={n}>{`متوفر ${q}`}</option>)}</datalist>
            <Input data-testid="quick-part-qty" type="number" min="1" dir="ltr" value={qQty} onChange={(e) => setQQty(e.target.value)} placeholder="الكمية" className="h-11" />
            <Input data-testid="quick-part-cost" type="number" dir="ltr" value={qPartCost} onChange={(e) => setQPartCost(e.target.value)} placeholder="التكلفة" className="h-11" />
            <Button data-testid="quick-part-submit" disabled={qBusy} onClick={quickPart} className="h-11 gap-1.5 bg-blue-600 hover:bg-blue-700">
              {qBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />} تم تغيير القطعة
            </Button>
          </div>
          <p className="mt-1.5 text-[11px] text-slate-400">اختر الدبّاب ثم القطعة — إن كانت في المخزون تُخصم تلقائيًا، وتظهر في «سجل الاستهلاك» وسجل صيانة المركبة. اترك التكلفة فاضية لتُحسب من سعر المخزون.</p>
        </div>
      )}
      {overCount > 0 && (
        <div role="button" tabIndex={0} onClick={() => setFState("over")} className="cursor-pointer rounded-2xl border-r-4 border-r-rose-400 bg-rose-50 p-3 flex items-center gap-2 text-sm text-rose-700 hover:bg-rose-100" data-testid="maintenance-over-alert">
          <AlertTriangle className="h-4 w-4 shrink-0" /> {overCount} مركبة تجاوزت الحد المسموح في آخر تغيير زيت.
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : !data?.vehicles?.length ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-400" data-testid="maintenance-empty">
          لا توجد {isMoto ? "دراجات نارية" : "سيارات"} مسجّلة. أضِف المركبات من قسم المركبات أولًا.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid={`maintenance-kpis-${type}`}>
            {mKpis.map(([k, l, v, tone]) => (
              <button key={k} type="button" onClick={() => setFState(k)} data-testid={`maint-kpi-${k}`}
                className={`rounded-2xl border bg-white p-3 text-start transition hover:shadow-md ${fState === k ? "border-blue-400 ring-2 ring-blue-100" : "border-slate-200"}`}>
                <p className="text-xs text-slate-500">{l}</p><p className={`font-heading text-xl font-bold tabular-nums ${tone}`}>{v}</p>
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              data-testid={`maintenance-search-${type}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`ابحث برقم ${isMoto ? "الدراجة" : "المركبة"} أو اسم السائق...`}
              className="pr-9 pl-9 h-11 rounded-xl bg-white"
            />
            {search && (
              <button type="button" data-testid={`maintenance-search-clear-${type}`} onClick={() => setSearch("")}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {filteredVehicles.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-400 text-sm" data-testid={`maintenance-search-empty-${type}`}>
              {search ? `لا توجد نتائج مطابقة لبحثك "${search}".` : "لا توجد مركبات بهذه الحالة."}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredVehicles.map((v) => (
            <button key={v.vehicle_number} data-testid={`maint-vehicle-${v.vehicle_number}`} onClick={() => setSel(v)}
              className="text-right rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md hover:border-blue-300 transition-all">
              <div className="flex items-center justify-between">
                <span className="font-mono-nums font-extrabold text-slate-900" dir="ltr">{v.vehicle_number}</span>
                {v.over_limit_recent ? <span className="text-[11px] font-semibold text-rose-600 bg-rose-50 rounded-full px-2 py-0.5">تجاوز الحد</span>
                  : v.oil_count ? <span className="text-[11px] font-semibold text-emerald-600 bg-emerald-50 rounded-full px-2 py-0.5">مُتابَع</span>
                  : <span className="text-[11px] text-slate-400 bg-slate-50 rounded-full px-2 py-0.5">بدون سجل</span>}
              </div>
              {v.driver && <div className="text-xs text-slate-500 mt-1">{v.driver}</div>}
              {showPurchase && (
                <div className="text-xs text-slate-400 mt-0.5" data-testid={`maint-purchase-${v.vehicle_number}`}>
                  قيمة الشراء: <span className="font-mono-nums font-semibold text-emerald-600">{fmt(v.purchase_value)} ⃁</span>
                </div>
              )}
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg bg-slate-50 p-2">
                  <div className="text-slate-400">آخر عدّاد</div>
                  <div className="flex items-center justify-between gap-1.5">
                    <div className="font-mono-nums font-bold text-slate-700">{v.last_odometer != null ? fmt(v.last_odometer) : "—"}</div>
                    {v.last_photo_id && (
                      <ImageZoom src={api.fileViewUrl(v.last_photo_id)} alt="صورة العدّاد"
                        label={`${v.vehicle_number} · آخر عدّاد ${v.last_odometer != null ? fmt(v.last_odometer) : ""} كم`}
                        testid={`card-odometer-photo-${v.vehicle_number}`}
                        thumbClassName="h-8 w-11 shrink-0 object-cover rounded border border-slate-200 hover:ring-2 hover:ring-blue-400" />
                    )}
                  </div>
                </div>
                <div className="rounded-lg bg-slate-50 p-2"><div className="text-slate-400">التغيير القادم</div><div className="font-mono-nums font-bold text-slate-700">{v.next_due_odometer != null ? fmt(v.next_due_odometer) : "—"}</div></div>
              </div>
              <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-400">
                <span className="flex items-center gap-1"><Droplets className="h-3 w-3" /> {v.oil_count} زيت</span>
                {showMaintenance && <span className="flex items-center gap-1"><Wrench className="h-3 w-3" /> {v.maintenance_count} صيانة</span>}
              </div>
            </button>
          ))}
            </div>
          )}
        </>
      )}

      <VehicleDetail open={!!sel} onOpenChange={(o) => !o && setSel(null)} vehicle={sel}
        showPurchase={showPurchase} showMaintenance={showMaintenance} oilReadOnly={oilReadOnly} limit={limit} onChanged={() => { load(); loadCosts(); }} />

      {costs && costs.series.some((s) => s.total > 0) && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="maintenance-costs-report">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="text-sm font-heading font-bold text-slate-800 flex items-center gap-1.5"><Coins className="h-4 w-4 text-emerald-600" /> تقرير تكاليف الصيانة الشهري</div>
            <div className="text-xs text-slate-500">الإجمالي: <span className="font-mono-nums font-bold text-emerald-600">{fmt(costs.totals.total)} ⃁</span></div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full table-fixed min-w-[480px] text-sm text-right">
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-2.5 text-right whitespace-nowrap font-medium">الشهر</th>
                  <th className="px-4 py-2.5 text-right whitespace-nowrap font-medium">الزيت</th>
                  <th className="px-4 py-2.5 text-right whitespace-nowrap font-medium">القطع</th>
                  <th className="px-4 py-2.5 text-right whitespace-nowrap font-medium">الإجمالي</th>
                </tr>
              </thead>
              <tbody className="font-mono-nums">
                {costs.series.map((s) => (
                  <tr key={s.month} className="border-t border-slate-100">
                    <td className="px-4 py-2.5 font-sans font-semibold text-slate-700 whitespace-nowrap">{s.month}</td>
                    <td className="px-4 py-2.5 text-blue-600 whitespace-nowrap">{fmt(s.oil_cost)} <span className="text-[10px] text-slate-400">({s.oil_count})</span></td>
                    <td className="px-4 py-2.5 text-amber-600 whitespace-nowrap">{fmt(s.parts_cost)} <span className="text-[10px] text-slate-400">({s.parts_count})</span></td>
                    <td className="px-4 py-2.5 font-bold text-slate-800 whitespace-nowrap">{fmt(s.total)} ⃁</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-200 bg-slate-50 font-bold">
                  <td className="px-4 py-2.5 font-sans whitespace-nowrap">الإجمالي</td>
                  <td className="px-4 py-2.5 text-blue-600 font-mono-nums whitespace-nowrap">{fmt(costs.totals.oil)}</td>
                  <td className="px-4 py-2.5 text-amber-600 font-mono-nums whitespace-nowrap">{fmt(costs.totals.parts)}</td>
                  <td className="px-4 py-2.5 text-emerald-600 font-mono-nums whitespace-nowrap">{fmt(costs.totals.total)} ⃁</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
